export const links = {
  calendly: 'https://calendly.com/nigmatullinaregina/30min',
  contactPage: '/contact-page',
  portfolioPage: '/portfolio',
};

export const siteContent = {
  links,

  nav: [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Process', href: '/process' },
    { label: 'Types of Projects', href: '/cases' },
    { label: 'Projects', href: '/work-experience' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ],

  hero: {
    title: 'Building Web Products That Are Clear, Scalable, and Maintainable',
    titleStatic: 'Building Web Products That Are',
    titleRotating: ['Clear', 'Scalable', 'Maintainable'],
    subtitle:
      'MVPs, SaaS platforms, and internal tools — delivered with structured scope, predictable execution, and AI-powered automation.',
    description:
      'From first release to long-term iteration, I ship useful features quickly without sacrificing clarity or maintainability.',
    bullets: [
      'MVPs & SaaS platforms',
      'Dashboards, roles, payments',
      'Faster delivery via AI-powered automation',
    ],
    ctaPrimary: { label: 'Book a Call', href: links.calendly },
    ctaSecondary: { label: 'View Services', href: '/services' },
    microcopy: 'Intro call · Product goals, constraints, and next steps',
  },

  services: {
    anchorId: 'services',
    title: 'Services',
    subtitle:
      'Practical engineering for product teams and founders — with attention to scope, constraints, and what needs to work on day one.',
    items: [
      {
        title: 'MVP & SaaS Development',
        description:
          'Plan the core workflows, ship a first version fast, and iterate with a foundation that won’t collapse as requirements evolve.',
        icon: 'rocket',
      },
      {
        title: 'Web Applications & Platforms',
        description:
          'Build dashboards, role-based access, admin tooling, and integrations that support real operations—not just demos.',
        icon: 'layers',
      },
      {
        title: 'Business Automation',
        description:
          'Replace manual processes with reliable workflows that reduce busywork and speed up delivery.',
        icon: 'settings',
      },
      {
        title: 'Conversion-Focused Websites',
        description:
          'Create fast, structured landing pages and marketing sites designed around clear messaging, flows, and measurable actions.',
        icon: 'sparkle',
      },
    ],
  },

  process: {
    anchorId: 'process',
    title: 'Process',
    subtitle:
      'A simple, repeatable workflow that keeps momentum while reducing surprises.',
    steps: [
      {
        title: 'Discovery & Goals',
        description:
          'Align on the user, the business goal, and what “done” means for the next release.',
        bullets: [
          'Define the core user journey',
          'Clarify constraints and priorities',
          'Decide what to ship first',
        ],
      },
      {
        title: 'Architecture & Scope',
        description:
          'Turn goals into a plan: data model, key screens, integrations, and a realistic scope.',
        bullets: [
          'Outline features and acceptance criteria',
          'Choose integration points',
          'Set the boundaries for v1',
        ],
      },
      {
        title: 'UX Logic & Structure',
        description:
          'Design the flow before writing code so implementation stays focused and consistent.',
        bullets: [
          'Information architecture and navigation',
          'States, permissions, and edge cases',
          'Copy and UI behavior guidelines',
        ],
      },
      {
        title: 'Development',
        description:
          'Build in small, reviewable increments with attention to maintainability and predictable behavior.',
        bullets: [
          'Implement features in slices',
          'Keep the codebase organized',
          'Validate key flows early',
        ],
      },
      {
        title: 'Launch & Support',
        description:
          'Ship confidently and keep improving based on real usage and feedback.',
        bullets: [
          'Production readiness checks',
          'Bug fixes and iteration',
          'Ongoing improvements as needs change',
        ],
      },
    ],
  },

  cases: {
    anchorId: 'cases',
    title: 'Types of Projects I Usually Take',
    subtitle:
      'I don’t take many projects at the same time. These are the kinds of work where I can stay involved and be genuinely useful.',
    items: [
      {
        title: 'MVP or First Product Version',
        description:
          'Building an initial version around a small set of core workflows, with room to adapt as the product evolves.',
        tags: ['Auth & roles', 'Dashboards', 'Payments', 'Admin'],
      },
      {
        title: 'SaaS UI or Frontend Refactor',
        description:
          'Improving structure and consistency in an existing frontend so it’s easier to maintain and iterate on.',
        tags: ['Design systems', 'Theming', 'Micro-frontends', 'Cleanup'],
      },
      {
        title: 'Internal Tools and Automation',
        description:
          'Replacing manual processes with simple tools, dashboards, and integrations that teams actually use.',
        tags: ['Workflows', 'Integrations', 'Ops tools'],
      },
      {
        title: 'Website + Product Entry Point',
        description:
          'Marketing or informational websites that connect cleanly to the product or platform behind them.',
        tags: ['Landing pages', 'Performance', 'Product entry'],
      },
    ],
  },

  projects: {
    anchorId: 'projects',
  },

  faq: {
    anchorId: 'faq',
    title: 'FAQ',
    items: [
      {
        q: 'Can you help with early-stage products?',
        a: 'Yes. I can shape the first release around a small set of valuable workflows, so you can ship quickly and learn from real users.',
      },
      {
        q: 'How do you ship faster without cutting corners?',
        a: 'I use AI-assisted workflows and automation to remove repetitive steps, keep feedback tight, and deliver clean, maintainable releases sooner.',
      },
      {
        q: 'What if requirements change during development?',
        a: 'That’s normal. I work in small milestones, keep scope explicit, and adjust priorities without losing the structure of the product.',
      },
      {
        q: 'Do you provide ongoing support after launch?',
        a: 'Yes. Support can include bug fixes, improvements, new features, and maintenance—based on what your product needs next.',
      },
      {
        q: 'Do I need to manage the project day-to-day?',
        a: 'No. I’ll propose a clear plan, share progress regularly, and ask for decisions only when they’re needed to move forward.',
      },
      {
        q: 'What tech stack do you use?',
        a: 'I choose tools based on your product’s needs and team constraints. If you already have a stack, I can usually work within it.',
      },
    ],
  },

  cta: {
    title: 'Let’s Build Something That Makes Sense',
    description:
      'If you have a product idea or an existing system that needs structure and reliability, I can help you ship the next version faster with a clear plan.',
    button: { label: 'Book a Call', href: links.calendly },
  },

  contact: {
    title: 'Book a Call',
    description:
      'Pick a time that works for you. The call is focused on your product, constraints, and next steps.',
    anchorId: 'contact',
  },
};
